import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, AlertCircle, CheckCircle2, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { getLogo, BRANDING } from '../config/branding';
import { getUserFriendlyError } from '../utils/errorMessages';
import { isValidEmail, validatePassword, sanitizeName } from '../utils/sanitize';

// Countries with flags - users just tap their flag!
const COUNTRIES = [
  { code: 'US', name: 'United States', flag: '🇺🇸', lang: 'en' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', lang: 'en' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', lang: 'en', hasMultiLang: true },
  { code: 'AU', name: 'Australia', flag: '🇦🇺', lang: 'en' },
  { code: 'DE', name: 'Deutschland', flag: '🇩🇪', lang: 'de' },
  { code: 'FR', name: 'France', flag: '🇫🇷', lang: 'fr' },
  { code: 'IN', name: 'India', flag: '🇮🇳', lang: 'en' },
  { code: 'ID', name: 'Indonesia', flag: '🇮🇩', lang: 'id' },
  { code: 'JP', name: '日本', flag: '🇯🇵', lang: 'ja' },
  { code: 'KR', name: '대한민국', flag: '🇰🇷', lang: 'ko' },
  { code: 'BR', name: 'Brasil', flag: '🇧🇷', lang: 'pt' },
  { code: 'PH', name: 'Philippines', flag: '🇵🇭', lang: 'en' },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬', lang: 'en' },
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬', lang: 'en' },
];

// Canada language options
const CANADA_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
];

const SignUp = () => {
  const navigate = useNavigate();
  const { loginWithGoogle, signup, isAuthenticated, loading } = useAuth();
  const { setCountry, setLanguage } = useLocale();
  const [step, setStep] = useState(3); // Skip country/language selection - go directly to signup form
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    gender: 'other'
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localeInitialized, setLocaleInitialized] = useState(false);

  // Auto-detect country, language, timezone, and currency on mount (run only once)
  useEffect(() => {
    if (localeInitialized) return; // Prevent re-running
    
    // Get timezone for accurate country detection
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const browserLang = navigator.language || navigator.userLanguage || 'en';
    const langCode = browserLang.split('-')[0]?.toLowerCase() || 'en';
    
    // Map timezones to countries (more accurate than language)
    const timezoneToCountry = {
      // United States
      'America/New_York': 'US', 'America/Chicago': 'US', 'America/Denver': 'US',
      'America/Los_Angeles': 'US', 'America/Phoenix': 'US', 'America/Anchorage': 'US',
      'Pacific/Honolulu': 'US',
      // United Kingdom
      'Europe/London': 'GB',
      // Canada
      'America/Toronto': 'CA', 'America/Vancouver': 'CA', 'America/Edmonton': 'CA',
      'America/Winnipeg': 'CA', 'America/Halifax': 'CA', 'America/Montreal': 'CA',
      // Australia
      'Australia/Sydney': 'AU', 'Australia/Melbourne': 'AU', 'Australia/Brisbane': 'AU',
      'Australia/Perth': 'AU', 'Australia/Adelaide': 'AU',
      // Germany
      'Europe/Berlin': 'DE',
      // France
      'Europe/Paris': 'FR',
      // India
      'Asia/Kolkata': 'IN', 'Asia/Calcutta': 'IN',
      // Indonesia
      'Asia/Jakarta': 'ID',
      // Japan
      'Asia/Tokyo': 'JP',
      // South Korea
      'Asia/Seoul': 'KR',
      // Brazil
      'America/Sao_Paulo': 'BR', 'America/Rio_Branco': 'BR',
      // Philippines
      'Asia/Manila': 'PH',
      // Singapore
      'Asia/Singapore': 'SG',
      // Nigeria
      'Africa/Lagos': 'NG',
    };
    
    // Try timezone first, then browser language region, then default
    let countryCode = timezoneToCountry[timezone];
    
    if (!countryCode) {
      // Fallback to browser language region (e.g., "en-GB" → "GB")
      const langParts = browserLang.split('-');
      if (langParts.length > 1) {
        countryCode = langParts[1].toUpperCase();
      }
    }
    
    // Find matching country or try to infer from language
    let detected = COUNTRIES.find(c => c.code === countryCode);
    
    if (!detected) {
      // Infer country from language code
      const langToCountry = {
        'de': 'DE', 'fr': 'FR', 'ja': 'JP', 'ko': 'KR', 
        'pt': 'BR', 'id': 'ID'
      };
      countryCode = langToCountry[langCode];
      detected = COUNTRIES.find(c => c.code === countryCode);
    }
    
    // Final fallback to US
    if (!detected) {
      detected = COUNTRIES[0]; // US
    }
    
    setSelectedCountry(detected);
    setSelectedLanguage(detected.lang || langCode);
    
    // Auto-save to locale context
    if (setCountry) setCountry(detected.code);
    if (setLanguage) setLanguage(detected.lang || langCode);
    
    setLocaleInitialized(true);
    console.log(`🌍 Auto-detected: Timezone=${timezone}, Country=${detected.code} (${detected.name}), Language=${detected.lang || langCode}`);
  }, [localeInitialized, setCountry, setLanguage]);

  // These handlers are kept for backwards compatibility but won't be used
  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    if (setCountry) setCountry(country.code);
    if (country.hasMultiLang) {
      setStep(2);
    } else {
      if (setLanguage) setLanguage(country.lang);
      setSelectedLanguage(country.lang);
      setStep(3);
    }
  };

  const handleLanguageSelect = (lang) => {
    setSelectedLanguage(lang);
    if (setLanguage) setLanguage(lang);
    setStep(3);
  };

  useEffect(() => {
    if (isAuthenticated && !loading) {
      navigate('/home');
    }
  }, [isAuthenticated, loading, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleGenderChange = (value) => {
    setFormData({
      ...formData,
      gender: value
    });
  };

  const handleGoogleSignup = () => {
    loginWithGoogle();
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    setError('');

    // Validate name
    const sanitizedName = sanitizeName(formData.name);
    if (!sanitizedName || sanitizedName.length < 2) {
      setError('Please enter your name (at least 2 characters)');
      return;
    }

    // Validate email format
    if (!isValidEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate password strength
    const passwordCheck = validatePassword(formData.password);
    if (!passwordCheck.valid) {
      setError(passwordCheck.message);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);

    try {
      console.log('📝 Submitting signup form...');
      await signup({
        name: sanitizedName,
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        gender: formData.gender
      });
      console.log('✅ Signup complete, navigating to home...');
      navigate('/home');
    } catch (err) {
      console.error('❌ Signup form error:', err);
      const friendlyError = getUserFriendlyError(err, 'Unable to create your account. Please try again.');
      setError(friendlyError);
      
      // Show additional help for network errors
      if (err.message && err.message.toLowerCase().includes('network')) {
        setError('Network error. Please check your internet connection and try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-white flex items-center justify-center">
        <div className="text-center">
          <img
            src={getLogo()}
            alt={BRANDING.app.name}
            className="h-16 w-16 mx-auto mb-4 rounded-2xl"
          />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Step 1: Country Selection (flags only - no text needed!)
  if (step === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50/80 via-cyan-50/60 to-white flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4 py-6">
          <div className="w-full max-w-sm animate-fade-in-up">
            {/* Logo */}
            <div className="text-center mb-6">
              <img
                src={getLogo()}
                alt={BRANDING.app.name}
                className="h-16 w-16 mx-auto mb-3 rounded-xl shadow-soft"
              />
              <span className="text-3xl">🌍</span>
            </div>
            
            {/* Country Grid - Just flags! */}
            <div className="grid grid-cols-4 gap-2 mb-6">
              {COUNTRIES.map((country) => (
                <button
                  key={country.code}
                  onClick={() => handleCountrySelect(country)}
                  className={`p-3 rounded-xl border-2 text-center transition-all duration-200 ${
                    selectedCountry?.code === country.code
                      ? 'bg-teal-100 border-teal-400 scale-110 shadow-lg'
                      : 'bg-white border-gray-200 hover:border-teal-300 hover:scale-105'
                  }`}
                >
                  <span className="text-2xl">{country.flag}</span>
                </button>
              ))}
            </div>
            
            {/* Already have account */}
            <p className="text-center text-sm text-gray-500">
              <Link to="/signin" className="text-teal-600 hover:text-teal-700 font-medium">
                Sign in →
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Language Selection (only for Canada)
  if (step === 2 && selectedCountry?.hasMultiLang) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50/80 via-cyan-50/60 to-white flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4 py-6">
          <div className="w-full max-w-sm animate-fade-in-up">
            {/* Selected Country */}
            <div className="text-center mb-8">
              <span className="text-5xl mb-2 block">{selectedCountry.flag}</span>
            </div>
            
            {/* Language Options */}
            <div className="space-y-3 mb-6">
              {CANADA_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageSelect(lang.code)}
                  className="w-full p-4 rounded-xl border-2 text-left transition-all duration-200 flex items-center justify-between bg-white border-gray-200 hover:border-teal-300 hover:bg-teal-50"
                >
                  <span className="font-semibold text-gray-800 text-lg">{lang.name}</span>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </button>
              ))}
            </div>
            
            {/* Back */}
            <button
              onClick={() => setStep(1)}
              className="w-full p-2 text-sm text-gray-500 hover:text-gray-700"
            >
              ←
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Signup Form
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50/80 via-cyan-50/60 to-white flex flex-col justify-between">
      {/* Top spacing */}
      <div className="pt-6"></div>
      
      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-4">
        <div className="w-full max-w-sm animate-fade-in-up">
          {/* Logo & Brand with selected country */}
          <div className="text-center mb-5">
            <div className="relative inline-block">
              <img
                src={getLogo()}
                alt={BRANDING.app.name}
                className="h-14 w-14 mx-auto mb-3 rounded-xl shadow-soft transition-transform hover:scale-105"
              />
              {selectedCountry && (
                <button 
                  onClick={() => setStep(1)}
                  className="absolute -right-2 -bottom-1 text-xl hover:scale-110 transition-transform"
                  title="Change country"
                >
                  {selectedCountry.flag}
                </button>
              )}
            </div>
            <h1 className="text-lg font-semibold text-gradient-purple mb-0.5">
              {BRANDING.app.name}
            </h1>
            <p className="text-gray-400 text-xs font-light tracking-wide">
              Your personal assistant for everyday life.
            </p>
          </div>

          {/* Sign Up Form */}
          <div className="glass-strong rounded-3xl p-6 shadow-soft-lg border border-white/60 animate-scale-in">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Create your account</h2>
            
            <form onSubmit={handleEmailSignup} className="space-y-3.5">
              <div className="space-y-1">
                <Label htmlFor="name" className="text-sm font-medium text-gray-600">Name</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="What should I call you?"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={submitting}
                  className="h-11 bg-white/80 border-gray-200/80 focus:border-teal-400 focus:ring-teal-400/20 rounded-xl text-sm transition-all placeholder:text-gray-400"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="email" className="text-sm font-medium text-gray-600">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={submitting}
                  className="h-11 bg-white/80 border-gray-200/80 focus:border-teal-400 focus:ring-teal-400/20 rounded-xl text-sm transition-all placeholder:text-gray-400"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="gender" className="text-sm font-medium text-gray-600">Gender <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Select value={formData.gender} onValueChange={handleGenderChange}>
                  <SelectTrigger className="h-11 bg-white/80 border-gray-200/80 focus:border-teal-400 rounded-xl text-sm transition-all">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="other">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label htmlFor="password" className="text-sm font-medium text-gray-600">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Create a strong password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={submitting}
                  className="h-11 bg-white/80 border-gray-200/80 focus:border-teal-400 focus:ring-teal-400/20 rounded-xl text-sm transition-all placeholder:text-gray-400"
                />
                <p className="text-xs text-gray-400 mt-1">
                  8+ characters, uppercase, lowercase & number
                </p>
              </div>

              <div className="space-y-1">
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-600">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="Type it again"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  disabled={submitting}
                  className="h-11 bg-white/80 border-gray-200/80 focus:border-teal-400 focus:ring-teal-400/20 rounded-xl text-sm transition-all placeholder:text-gray-400"
                />
              </div>

              {error && (
                <Alert variant="destructive" className="border-red-200/60 bg-red-50/80 py-2.5 rounded-xl animate-fade-in">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-sm">{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full h-11 bg-gray-900 hover:bg-gray-800 text-white rounded-xl font-medium mt-1 shadow-soft transition-all hover:shadow-soft-lg hover:-translate-y-0.5 active:translate-y-0"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>

            {/* Sign In Link */}
            <div className="text-center mt-5 pt-4 border-t border-gray-100/60">
              <p className="text-gray-500 text-sm">
                Already have an account?{' '}
                <Link to="/signin" className="text-teal-600 hover:text-teal-700 font-semibold transition-colors">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Bottom section */}
      <footer className="pb-5 pt-3 text-center">
        <p className="text-xs text-gray-400 font-light">
          We're glad you're here. 💜
        </p>
      </footer>
    </div>
  );
};

export default SignUp;
